<script setup>
import { ref, onMounted, onUnmounted, onBeforeUnmount, computed, nextTick, watch } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import dayjs from "dayjs";
import useAppStore from "@/store/modules/app";
import {
  Chart,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
} from "chart.js";
import {
  getMovieReviewDailyService,
  getSocialStatsService,
} from "@/api/dp/index.js";
import XPlatformIcon from "@/layout/components/v2/x-platform-icon.vue";
import {
  destroyAllChartsUnderSelector,
  markClientV2DetailChartsDisposing,
} from "@/views/dp/client-v2/chart-nav-guard";

Chart.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip
);

const RD_CHART_EVENTS = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'];
const RD_CHART_INTERACTION = { mode: 'index', intersect: false };

const RD_CHART_ANIMATION = {
  duration: 2000,
  easing: 'easeOutQuart',
};

const RD_CHART_TRANSITIONS = {
  active: {
    animation: {
      duration: 400,
      easing: 'easeOutQuart',
    },
  },
  resize: { animation: { duration: 0 } },
};

const RD_CHART_TOOLTIP = {
  enabled: true,
  animation: {
    duration: 250,
    easing: 'easeOutQuart',
  },
};

const REVIEW_MOVIE_COLORS = [
  "#10B981",
  "#3B82F6",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
];
const reviewDashedTooltipMarkers = new Map();

const getReviewDashedTooltipMarker = (borderColor) => {
  const color = typeof borderColor === "string" ? borderColor : "#909399";
  const cached = reviewDashedTooltipMarkers.get(color);
  if (cached) return cached;
  if (typeof document === "undefined") return "line";
  const marker = document.createElement("canvas");
  marker.width = 16;
  marker.height = 10;
  const context = marker.getContext("2d");
  if (!context) return "line";
  context.strokeStyle = color;
  context.lineWidth = 2;
  context.setLineDash([4, 2]);
  context.beginPath();
  context.moveTo(1, marker.height / 2);
  context.lineTo(marker.width - 1, marker.height / 2);
  context.stroke();
  reviewDashedTooltipMarkers.set(color, marker);
  return marker;
};

const reviewComparisonLegend = (color) => ({
  position: "top",
  align: "start",
  fullSize: true,
  labels: {
    usePointStyle: true,
    boxWidth: 18,
    boxHeight: 8,
    pointStyleWidth: 22,
    padding: 12,
    color,
    font: { size: 11, lineHeight: 1.2 },
    sort: (a, b) => (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0),
    generateLabels(chart) {
      const generateDefaultLabels = Chart.defaults.plugins.legend.labels.generateLabels;
      const labels = generateDefaultLabels ? generateDefaultLabels(chart) : [];
      return labels.map((item) => {
        const dataset = chart.data.datasets[item.datasetIndex];
        const isLine = dataset?.type === "line";
        const isScore = dataset?.metricKey === "rating";
        return {
          ...item,
          pointStyle: isLine ? "line" : "rectRounded",
          lineDash: isScore ? [6, 4] : [],
        };
      });
    },
  },
});

const reviewComparisonTooltip = {
  ...RD_CHART_TOOLTIP,
  usePointStyle: true,
  itemSort: (a, b) => (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0),
  callbacks: {
    label(context) {
      const label = context.dataset.label ?? "";
      const value = context.raw;
      if (value == null) return `${label}: -`;
      return context.dataset.metricKey === "rating"
        ? `${label}: ${Number(value).toFixed(1)}`
        : `${label}: ${Number(value).toLocaleString()}`;
    },
    labelPointStyle(context) {
      const isLine = context.dataset.type === "line";
      const isScore = context.dataset.metricKey === "rating";
      return {
        pointStyle: isScore
          ? getReviewDashedTooltipMarker(context.dataset.borderColor)
          : isLine
            ? "line"
            : "rectRounded",
        rotation: 0,
      };
    },
  },
};

const reviewComparisonCompleteTooltipPlugin = {
  id: "reviewComparisonCompleteTooltip",
  afterEvent(chart, args) {
    const event = args.event;
    const tooltip = chart.tooltip;
    if (!tooltip || !args.inChartArea || event.type === "mouseout") return;
    const dataIndex = chart.getActiveElements()[0]?.index;
    if (dataIndex == null) return;
    const allVisibleItems = chart.data.datasets.flatMap((dataset, datasetIndex) => (
      chart.isDatasetVisible(datasetIndex) && dataIndex < (dataset.data?.length ?? 0)
        ? [{ datasetIndex, index: dataIndex }]
        : []
    ));
    tooltip.setActiveElements(allVisibleItems, { x: event.x, y: event.y });
    args.changed = true;
  },
};

const RD_CHART_ELEMENTS = {
  point: {
    radius: 0,
    hoverRadius: 8,
    hitRadius: 12,
    hoverBorderWidth: 2,
  },
};

const waitRdAnimationFrames = (count = 5) => new Promise((resolve) => {
  let left = count;
  const step = () => {
    if (left <= 0) {
      resolve();
      return;
    }
    left -= 1;
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
});

const muteRdChartAnimations = (chart) => {
  try {
    chart.options.animation = false;
    chart.options.responsive = false;
    if (chart.config?.options) chart.config.options.animation = false;
    chart.data?.datasets?.forEach((ds) => {
      ds.animation = false;
      ds.fill = false;
    });
    if (chart.options.transitions) {
      chart.options.transitions.active = { animation: { duration: 0 } };
      chart.options.transitions.resize = { animation: { duration: 0 } };
    }
    if (chart.options.plugins?.tooltip) {
      chart.options.plugins.tooltip.animation = { duration: 0 };
    }
  } catch {
    /* ignore */
  }
};

let rdChartDisposePromise = null;
let rdChartInitSeq = 0;
/** false 时从 DOM 移除 canvas，避免离开路由后 Animator 仍在 draw */
const rdChartsMounted = ref(true);

const resetRdChartDisposeState = () => {
  rdChartDisposePromise = null;
};

const route = useRoute();
const router = useRouter();

const movieId = computed(() => String(route.query.movieId ?? ""));
const movieName = computed(() => String(route.query.movieName ?? ""));
const dateFrom = computed(() => Number(route.query.dateFrom));
const dateTo = computed(() => Number(route.query.dateTo));
const detailSource = computed(() => {
  const s = String(route.query.source ?? "review");
  return s === "youtube" || s === "instagram" || s === "twitter" ? s : "review";
});
const isSocialDetail = computed(
  () => detailSource.value === "youtube" || detailSource.value === "instagram" || detailSource.value === "twitter",
);
const instagramHashtag = computed(() => String(route.query.instagramHashtag ?? ""));
const twitterHashtag = computed(() => String(route.query.twitterHashtag ?? ""));

const pageTitle = computed(() => {
  if (detailSource.value === "youtube") return "YouTube予告編再生回数 詳細";
  if (detailSource.value === "instagram") return "Instagram ハッシュタグポスト数 詳細";
  if (detailSource.value === "twitter") return "X インデックスのトレンド 詳細";
  return "評価 & エンゲージメント 詳細";
});

const pageTitleIcon = computed(() => {
  if (detailSource.value === "youtube") return "fab fa-youtube";
  if (detailSource.value === "instagram") return "fab fa-instagram";
  if (detailSource.value === "twitter") return "fab fa-x-twitter";
  return "fas fa-chart-line";
});

const chartSectionTitle = computed(() => {
  if (detailSource.value === "youtube") return "YouTube予告編再生回数 日別推移";
  if (detailSource.value === "instagram") return "Instagram ハッシュタグポスト数 日別推移";
  if (detailSource.value === "twitter") return "X インデックスのトレンド 日別推移";
  return "評価データ 日別推移：評価数 / スコア / 観たい / 観た";
});

/** SNS 統計のデータ取得期間（作品公開日基準）— index.vue の YouTube カードと同一文言 */
const SOCIAL_DATA_SOURCE_TIP =
  "データは作品の公開日の2か月前から、公開後2か月までを対象としています。";
const chartDataTipVisible = ref(false);
const dateLabel = computed(() => {
  if (!dateFrom.value || !dateTo.value) return "";
  return `${dayjs(dateFrom.value).format("YYYY/MM/DD")} 〜 ${dayjs(dateTo.value - 86400000).format("YYYY/MM/DD")}`;
});

// Charts
const engagementChartRef = ref(null);
const engagementChartInstance = ref(null);
const ratingChartRef = ref(null);
const ratingChartInstance = ref(null);
const reviewChartPack = ref(null);
const socialChartRef = ref(null);
const socialChartInstance = ref(null);

const device = computed(() => useAppStore().device);

/** 評価詳細ページ：モバイル全画面チャート */
const rdFullscreenChartKey = ref(null);
const rdFullscreenClosing = ref(false);
const RD_FS_ANIM_MS = 720;

function isFsDebugEnabled() {
  try {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("dpFsDebug") === "1") return true;
    }
    return localStorage.getItem("dpFsDebug") === "1";
  } catch {
    return false;
  }
}

/**
 * 模擬器（非真机）での挙動切替：模拟器判定の場合は
 * `screen.orientation.lock` を行わず、普通の fullscreen 表示だけにする。
 */
function isEmulatorLikeDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toString().toLowerCase();

  // iOS UA を偽装した desktop emulation の場合、
  // navigator.platform が iPhone/iPad/iPod にならないことが多い。
  // これを利用して「iPhone UA なのに platform が iPhone ではない」= 模擬端末と判定する。
  const uaIndicatesIos = ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod");
  const platformIndicatesIos =
    platform.includes("iphone") || platform.includes("ipad") || platform.includes("ipod");
  if (uaIndicatesIos && !platformIndicatesIos) return true;

  // 额外兜底：UA 像 iOS，但 platform 明显是桌面（mac/win/linux）=> 当作模拟器
  const platformLooksDesktop =
    platform.includes("mac") || platform.includes("win") || platform.includes("linux");
  if (uaIndicatesIos && platformLooksDesktop) return true;

  // さらにあなたが提示した iOS シミュレータ UA（精密一致）も追加で判定（オプション）
  const looksLikeProvidedIosSimulatorUa =
    ua.includes("iphone; cpu iphone os 18_5") &&
    ua.includes("mobile/15e148") &&
    ua.includes("safari/604.1");
  if (looksLikeProvidedIosSimulatorUa) return true;
  const indicators = [
    "android sdk built for x86",
    "generic_x86",
    "x86_64",
    "emulator",
    "genymotion",
    "goldfish",
    "ranchu",
    "sdk_gphone",
    "sdk_google",
    "test-keys",
  ];
  return indicators.some((i) => ua.includes(i));
}

const shouldAutoRotateOnFullscreen = !isEmulatorLikeDevice();

function logFsDebug(prefix, extra) {
  if (!isFsDebugEnabled()) return;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const platform = typeof navigator !== "undefined" ? (navigator.platform || "") : "";
  // eslint-disable-next-line no-console
  console.log(`[dp-chart-fs:${prefix}]`, {
    ua,
    platform,
    shouldAutoRotateOnFullscreen,
    ...extra,
  });
}

const rdFsLayoutSnapshot = new Map();

function captureRdChartContainerLayout(key) {
  const canvas = getRdCanvasForKey(key);
  const container = canvas?.parentElement;
  if (!container) return;
  const r = container.getBoundingClientRect();
  const w = Math.round(r.width);
  const h = Math.round(r.height);
  if (w > 0 && h > 0) {
    rdFsLayoutSnapshot.set(key, { w, h });
  }
}

function getScreenOrientationApi() {
  return typeof screen !== "undefined" && screen.orientation ? screen.orientation : null;
}

function lockLandscape() {
  const o = getScreenOrientationApi();
  if (!o?.lock) return;
  const candidates = ["landscape", "landscape-primary", "landscape-secondary"];
  const run = (i) => {
    if (i >= candidates.length) return;
    o.lock(candidates[i]).catch(() => run(i + 1));
  };
  run(0);
}

function lockPortrait() {
  const o = getScreenOrientationApi();
  if (!o) return;
  if (o.lock) {
    const candidates = ["portrait", "portrait-primary", "portrait-secondary"];
    const run = (i) => {
      if (i >= candidates.length) {
        tryUnlock();
        return;
      }
      o.lock(candidates[i]).catch(() => run(i + 1));
    };
    run(0);
    return;
  }
  tryUnlock();

  function tryUnlock() {
    if (o?.unlock) {
      try {
        o.unlock();
      } catch {
        /* ignore */
      }
    }
  }
}

function getRdChartCardEl(key) {
  return document.querySelector(
    `.review-detail-page [data-rd-chart-key="${key}"]`,
  );
}

function tryRequestDomFullscreenThenLockLandscape(key) {
  const autoRotate = shouldAutoRotateOnFullscreen;
  logFsDebug("enter", { key, autoRotate });
  // 模拟器/桌面仿真：仅使用页面内全屏视图，不进入浏览器原生全屏
  if (!autoRotate) {
    logFsDebug("enterSkipNativeFullscreen", { key, reason: "emulatorDetected" });
    return;
  }
  const el = getRdChartCardEl(key);
  if (!el) {
    if (autoRotate) lockLandscape();
    return;
  }
  const fn = el.requestFullscreen;
  if (typeof fn !== "function") {
    if (autoRotate) lockLandscape();
    return;
  }
  try {
    const p = fn.call(el);
    if (p && typeof p.then === "function") {
      p
        .then(() => {
          if (autoRotate) lockLandscape();
        })
        .catch(() => {
          if (autoRotate) lockLandscape();
        });
    } else {
      if (autoRotate) lockLandscape();
    }
  } catch {
    if (autoRotate) lockLandscape();
  }
}

function getRdCanvasForKey(key) {
  if (key === "engagement") return engagementChartRef.value;
  if (key === "rating") return ratingChartRef.value;
  if (key === "social") return socialChartRef.value;
  return null;
}

function getRdChartInstanceForKey(key) {
  if (key === "engagement") return engagementChartInstance.value;
  if (key === "rating") return ratingChartInstance.value;
  if (key === "social") return socialChartInstance.value;
  return null;
}

function resizeRdChartForKey(key) {
  const chart = getRdChartInstanceForKey(key);
  if (!chart) return;
  try {
    chart.resize();
  } catch {
    /* ignore */
  }
}

let rdFsViewportCleanup = null;

function detachRdFullscreenViewportListeners() {
  rdFsViewportCleanup?.();
  rdFsViewportCleanup = null;
}

function attachRdFullscreenViewportListeners() {
  detachRdFullscreenViewportListeners();
  const handler = () => {
    const k = rdFullscreenChartKey.value;
    if (k) resizeRdChartForKey(k);
  };
  window.addEventListener("orientationchange", handler);
  window.addEventListener("resize", handler);
  rdFsViewportCleanup = () => {
    window.removeEventListener("orientationchange", handler);
    window.removeEventListener("resize", handler);
  };
}

function clearRdChartContainerSizeLock(container) {
  if (!container) return;
  container.style.removeProperty("width");
  container.style.removeProperty("height");
  container.style.removeProperty("min-height");
  container.style.removeProperty("max-height");
  container.style.removeProperty("box-sizing");
}

function resetRdCanvasStylesAndResize(key) {
  const canvas = getRdCanvasForKey(key);
  const chart = getRdChartInstanceForKey(key);
  const snap = rdFsLayoutSnapshot.get(key);
  rdFsLayoutSnapshot.delete(key);

  if (!canvas || !chart) return;

  canvas.style.removeProperty("width");
  canvas.style.removeProperty("height");

  const container = canvas.parentElement;

  const applyResize = () => {
    try {
      chart.resize();
    } catch {
      /* ignore */
    }
  };

  if (container && snap && snap.w > 0 && snap.h > 0) {
    container.style.boxSizing = "border-box";
    container.style.width = `${snap.w}px`;
    container.style.height = `${snap.h}px`;
    container.style.minHeight = `${snap.h}px`;
    container.style.maxHeight = `${snap.h}px`;
  }

  requestAnimationFrame(() => {
    applyResize();
    if (snap && snap.w > 0 && snap.h > 0 && container) {
      requestAnimationFrame(() => {
        clearRdChartContainerSizeLock(container);
        applyResize();
      });
    }
  });
}

function closeRdFullscreenImmediate() {
  detachRdFullscreenViewportListeners();
  const key = rdFullscreenChartKey.value;
  logFsDebug("exitImmediate", { key });
  rdFullscreenClosing.value = false;
  rdFullscreenChartKey.value = null;
  document.body.style.overflow = "";
  try {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
  } catch {
    /* ignore */
  }
  if (shouldAutoRotateOnFullscreen) lockPortrait();
  if (key) {
    nextTick(() => resetRdCanvasStylesAndResize(key));
  }
}

function openRdFullscreen(key) {
  if (device.value !== "mobile") return;
  captureRdChartContainerLayout(key);
  rdFullscreenClosing.value = false;
  rdFullscreenChartKey.value = key;
  document.body.style.overflow = "hidden";
  attachRdFullscreenViewportListeners();
  tryRequestDomFullscreenThenLockLandscape(key);
  nextTick(() => {
    requestAnimationFrame(() => {
      resizeRdChartForKey(key);
      requestAnimationFrame(() => resizeRdChartForKey(key));
    });
  });
}

function closeRdFullscreen() {
  if (device.value !== "mobile") return;
  const key = rdFullscreenChartKey.value;
  if (!key) return;
  logFsDebug("exit", { key });
  rdFullscreenClosing.value = true;
  detachRdFullscreenViewportListeners();
  setTimeout(() => {
    rdFullscreenClosing.value = false;
    rdFullscreenChartKey.value = null;
    document.body.style.overflow = "";

    const doRestore = () => {
      if (shouldAutoRotateOnFullscreen) lockPortrait();
      resetRdCanvasStylesAndResize(key);
    };

    try {
      if (document.fullscreenElement) {
        const p = document.exitFullscreen?.();
        if (p && typeof p.then === "function") {
          p.then(doRestore).catch(doRestore);
        } else {
          doRestore();
        }
      } else {
        doRestore();
      }
    } catch {
      doRestore();
    }
  }, RD_FS_ANIM_MS);
}

// Data
const loading = ref(false);
// 图表渲染中：数据加载完到 chart 真正绘制完之间保持遮罩，避免出现空白 canvas
const chartRendering = ref(false);
const dailyData = ref([]);
const showRotateHint = ref(false);

const updateRotateHint = () => {
  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;
  showRotateHint.value = isMobile && isPortrait;
};

// Summary stats
const totalRateCount = computed(() => dailyData.value.reduce((s, d) => s + (d.rateCount ?? 0), 0));
const avgRating = computed(() => {
  const rated = dailyData.value.filter(d => d.rating != null && d.rateCount > 0);
  if (!rated.length) return null;
  const weighted = rated.reduce((s, d) => s + d.rating * d.rateCount, 0);
  const total = rated.reduce((s, d) => s + d.rateCount, 0);
  return total > 0 ? (weighted / total).toFixed(2) : null;
});
const latestWantToSee = computed(() => {
  const last = [...dailyData.value].reverse().find(d => d.wantToSee != null);
  return last ? last.wantToSee : 0;
});
const latestHaveSeen = computed(() => {
  const last = [...dailyData.value].reverse().find(d => d.haveSeen != null);
  return last ? last.haveSeen : 0;
});

/** 销毁前静音动画，避免 Animator 在 ctx 释放后仍调用 clipArea/save */
const destroyRdChartInstance = async (instRef, canvas) => {
  const chart = instRef.value ?? (canvas ? Chart.getChart(canvas) : null);
  instRef.value = null;
  if (!chart) {
    if (canvas) canvas.style.pointerEvents = 'auto';
    return;
  }
  const chartCanvas = chart.canvas ?? canvas;
  if (chartCanvas) chartCanvas.style.pointerEvents = 'none';
  try {
    chart.options.events = [];
  } catch {
    /* ignore */
  }
  muteRdChartAnimations(chart);
  try {
    chart.stop();
  } catch {
    /* ignore */
  }
  for (let i = 0; i < 12; i += 1) {
    if (!chart.animating) break;
    try {
      chart.stop();
    } catch {
      /* ignore */
    }
    await waitRdAnimationFrames(1);
  }
  await waitRdAnimationFrames(3);
  try {
    if (chart.ctx) chart.destroy();
  } catch {
    /* ignore */
  }
  if (chartCanvas) chartCanvas.style.pointerEvents = 'auto';
};

const pickSocialStatsPayload = (res) => {
  if (!res || typeof res !== "object") return null;
  if (res.youtube || res.instagram || res.twitter) return res;
  if (res.data && typeof res.data === "object" && (res.data.youtube || res.data.instagram || res.data.twitter)) {
    return res.data;
  }
  return null;
};

const pickSocialDailyList = (payload, platform) => {
  const block = payload?.[platform];
  if (!block || !Array.isArray(block.dailyList)) return [];
  return block.dailyList
    .map((item) => {
      const rawDate = item?.date;
      const d = rawDate != null ? dayjs(rawDate) : null;
      const total = Number(item?.total);
      if (!d?.isValid() || Number.isNaN(total)) return null;
      // twitter のみ densityIndex（0~100）を併せて携帯する。
      // 非数値/欠落は null（行は破棄せず total/date は保持し、グラフ側で断線させる）
      let densityIndex = null;
      if (platform === "twitter") {
        const di = Number(item?.densityIndex);
        densityIndex =
          item?.densityIndex == null || item?.densityIndex === "" || Number.isNaN(di)
            ? null
            : di;
      }
      return {
        ...item,
        date: d.format("YYYY/MM/DD"),
        total,
        densityIndex,
      };
    })
    .filter(Boolean)
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
};

const emptyReviewDailyRow = (d) => ({
  date: d.format("YYYY/MM/DD"),
  rateCount: 0,
  wantToSee: 0,
  haveSeen: 0,
  rating: null,
  rate5Count: 0,
  rate4Count: 0,
  rate3Count: 0,
  rate2Count: 0,
  rate1Count: 0,
});

/** 評価詳細：欠落日は各指標 0 / rating null で補完 */
const fillReviewDailyRange = (list, fromMs, toMs) => {
  if (!fromMs || !toMs) return list;
  const dataMap = new Map();
  (list || []).forEach((item) => {
    if (item?.date == null) return;
    const d = dayjs(item.date);
    if (!d.isValid()) return;
    dataMap.set(d.format("YYYY-MM-DD"), item);
  });
  const filled = [];
  let current = dayjs(fromMs).startOf("day");
  const endExclusive = dayjs(toMs).startOf("day");
  while (current.isBefore(endExclusive)) {
    const key = current.format("YYYY-MM-DD");
    filled.push(dataMap.get(key) ?? emptyReviewDailyRow(current));
    current = current.add(1, "day");
  }
  return filled;
};

const pickReviewDailyList = (res) => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === "object") {
    if (Array.isArray(res.reviewDailyList)) return res.reviewDailyList;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.list)) return res.list;
    if (Array.isArray(res.rows)) return res.rows;
  }
  return [];
};

/** 所選作品のレビュー日次データを X 軸込みの単一系列 chart pack に変換する */
const buildReviewChartPack = (rows) => {
  const axisRows = (rows || [])
    .map((row) => {
      if (!row?.date || !dayjs(row.date).isValid()) return null;
      return { ...row, date: dayjs(row.date).startOf("day").format("YYYY-MM-DD") };
    })
    .filter(Boolean)
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
  if (!axisRows.length) return null;
  const dates = axisRows.map((row) => row.date);
  const spansYears = dates.some((date) => dayjs(date).year() !== dayjs(dates[0]).year());
  const labels = dates.map((date) => dayjs(date).format(spansYears ? "YYYY/MM/DD" : "MM/DD"));
  const toNumberOrNull = (value) => {
    if (value == null || value === "") return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
  };
  const pick = (key) => axisRows.map((row) => toNumberOrNull(row[key]));
  const wantToSee = pick("wantToSee");
  const haveSeen = pick("haveSeen");
  const rateCount = pick("rateCount");
  const rating = pick("rating");
  const nonZero = (arr) => arr.some((v) => v != null && v !== 0);

  return {
    labels,
    dates,
    series: [{
      label: movieName.value || "選択中の作品",
      movieId: movieId.value,
      wantToSee,
      haveSeen,
      rateCount,
      rating,
      hasWantToSee: nonZero(wantToSee),
      hasHaveSeen: nonZero(haveSeen),
      hasRateCount: nonZero(rateCount),
      hasRating: nonZero(rating),
    }],
  };
};

/** SNS ごとの socialStats.dailyList を単一系列 chart pack に変換する */
const buildSocialChartPack = (rows, platform) => {
  const axisRows = (rows || [])
    .map((row) => {
      if (!row?.date || !dayjs(row.date).isValid()) return null;
      const date = dayjs(row.date).startOf("day").format("YYYY-MM-DD");
      const sourceValue = platform === "twitter" ? row.densityIndex : row.total;
      const numericValue = sourceValue == null || sourceValue === "" ? null : Number(sourceValue);
      return { ...row, date, value: Number.isNaN(numericValue) ? null : numericValue };
    })
    .filter(Boolean)
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
  if (!axisRows.length) return null;
  const dates = axisRows.map((row) => row.date);
  const spansYears = dates.some((date) => dayjs(date).year() !== dayjs(dates[0]).year());
  const labels = dates.map((date) => dayjs(date).format(spansYears ? "YYYY/MM/DD" : "MM/DD"));

  return {
    labels,
    dates,
    series: [{
      label: movieName.value || "選択中の作品",
      movieId: movieId.value,
      values: axisRows.map((row) => row.value),
    }],
  };
};

const initSocialChart = (pack, platform) => {
  const seq = ++rdChartInitSeq;
  void (async () => {
  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  if (!socialChartRef.value) return;
  await destroyRdChartInstance(socialChartInstance, socialChartRef.value);
  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  const canvas = socialChartRef.value;
  const ctx = canvas.getContext("2d");
  if (!ctx || !pack?.labels?.length || !pack?.series?.length) return;
  canvas.style.pointerEvents = 'auto';

  const isYoutube = platform === "youtube";
  const isTwitter = platform === "twitter";
  const isDark = document.documentElement.classList.contains("dark");
  const chartGrid = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";
  let chartTick;
  let chartLegend;
  try {
    const css = getComputedStyle(document.documentElement);
    chartTick = css.getPropertyValue("--text-secondary").trim() || undefined;
    chartLegend = css.getPropertyValue("--text-primary").trim() || undefined;
  } catch {
    /* ignore */
  }
  const seriesLabel = isYoutube ? "再生回数" : isTwitter ? "投稿密集度（日次）" : "ポスト数";
  const datasets = pack.series.map((movie, index) => {
    const color = REVIEW_MOVIE_COLORS[index % REVIEW_MOVIE_COLORS.length];
    return {
      label: movie.label,
      metricKey: platform,
      data: movie.values,
      borderColor: color,
      backgroundColor: "transparent",
      borderWidth: 2,
      tension: 0.35,
      fill: false,
      spanGaps: true,
      pointRadius: 3,
      pointStyle: "circle",
      pointHitRadius: 8,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: color,
    };
  });
  const allValues = pack.series.flatMap((movie) => (
    movie.values.filter((value) => Number.isFinite(value))
  ));
  const positiveValues = allValues.filter((value) => value > 0);
  const minPositiveValue = positiveValues.length ? Math.min(...positiveValues) : 0;
  const maxPositiveValue = positiveValues.length ? Math.max(...positiveValues) : 0;
  // 例：11,000,000 対 1,600 のような差では線形軸だと小さい作品が基線に潰れる。
  const useLogScale = !isTwitter
    && minPositiveValue > 0
    && maxPositiveValue / minPositiveValue >= 100;
  const twitterMaxValue = isTwitter
    ? allValues.reduce((acc, value) => (value > acc ? value : acc), 0)
    : 0;
  const twitterYMax = Math.min(100, Math.max(10, Math.ceil(twitterMaxValue * 1.2)));

  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  socialChartInstance.value = new Chart(ctx, {
    type: "line",
    plugins: [reviewComparisonCompleteTooltipPlugin],
    data: {
      labels: pack.labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...RD_CHART_ANIMATION },
      events: [...RD_CHART_EVENTS],
      transitions: { ...RD_CHART_TRANSITIONS },
      interaction: { ...RD_CHART_INTERACTION },
      elements: { ...RD_CHART_ELEMENTS },
      plugins: {
        legend: reviewComparisonLegend(chartLegend),
        tooltip: reviewComparisonTooltip,
      },
      scales: {
        x: {
          grid: { color: chartGrid },
          ticks: { maxRotation: 45, minRotation: 0, color: chartTick },
        },
        y: {
          type: useLogScale ? "logarithmic" : "linear",
          beginAtZero: !useLogScale,
          // twitter（densityIndex）は 0 始点を維持しつつ上限をデータに応じて動的調整
          // （下限 10・上限 100）。youtube/instagram は従来どおり完全自動スケール。
          ...(isTwitter ? { min: 0, max: twitterYMax } : {}),
          grid: { color: chartGrid },
          ticks: {
            color: chartTick,
            callback: (value) => Number(value).toLocaleString(),
          },
          title: {
            display: true,
            text: useLogScale ? `${seriesLabel}（対数）` : seriesLabel,
            color: chartTick,
          },
        },
      },
    },
  });
  })();
};

const reviewColorWithAlpha = (hex, alpha) => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return `rgba(0, 102, 255, ${alpha})`;
  return `rgba(${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(match[3], 16)}, ${alpha})`;
};

const mixReviewMetricColor = (hex, targetHex, ratio = 0.32) => {
  const parseHex = (value) => {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value);
    return match ? [1, 2, 3].map((index) => parseInt(match[index], 16)) : null;
  };
  const source = parseHex(hex);
  const target = parseHex(targetHex);
  if (!source || !target) return hex;
  const mixed = source.map((channel, index) => (
    Math.round(channel + (target[index] - channel) * ratio)
      .toString(16)
      .padStart(2, "0")
  ));
  return `#${mixed.join("")}`;
};

const initCharts = (pack) => {
  const seq = ++rdChartInitSeq;
  void (async () => {
  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  if (!engagementChartRef.value || !ratingChartRef.value) return;
  await destroyRdChartInstance(engagementChartInstance, engagementChartRef.value);
  await destroyRdChartInstance(ratingChartInstance, ratingChartRef.value);
  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;

  const engagementCanvas = engagementChartRef.value;
  const ratingCanvas = ratingChartRef.value;
  const engagementCtx = engagementCanvas.getContext("2d");
  const ratingCtx = ratingCanvas.getContext("2d");
  if (!engagementCtx || !ratingCtx) return;
  engagementCanvas.style.pointerEvents = 'auto';
  ratingCanvas.style.pointerEvents = 'auto';

  const isDark = document.documentElement.classList.contains("dark");
  const chartGrid = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)";
  let chartTick;
  let chartLegend;
  try {
    const css = getComputedStyle(document.documentElement);
    chartTick = css.getPropertyValue("--text-secondary").trim() || undefined;
    chartLegend = css.getPropertyValue("--text-primary").trim() || undefined;
  } catch {
    /* ignore */
  }

  const dates = pack.labels;
  const series = Array.isArray(pack.series) ? pack.series : [];
  const engagementDatasets = series.flatMap((movie, index) => {
    const color = REVIEW_MOVIE_COLORS[index % REVIEW_MOVIE_COLORS.length];
    const datasets = [];
    if (movie.hasWantToSee) {
      datasets.push({
        label: `${movie.label}・観たい`,
        metricKey: "wantToSee",
        data: movie.wantToSee,
        backgroundColor: reviewColorWithAlpha(color, 0.35),
        borderColor: color,
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: "y",
        order: 2,
      });
    }
    if (movie.hasHaveSeen) {
      datasets.push({
        label: `${movie.label}・観た`,
        metricKey: "haveSeen",
        data: movie.haveSeen,
        backgroundColor: reviewColorWithAlpha(color, 0.68),
        borderColor: color,
        borderWidth: 2,
        borderRadius: 4,
        yAxisID: "y3",
        order: 2,
      });
    }
    return datasets;
  });
  const ratingDatasets = series.flatMap((movie, index) => {
    const color = REVIEW_MOVIE_COLORS[index % REVIEW_MOVIE_COLORS.length];
    const scoreColor = mixReviewMetricColor(
      color,
      isDark ? "#FFFFFF" : "#111827",
      isDark ? 0.38 : 0.32,
    );
    const datasets = [];
    if (movie.hasRateCount) {
      datasets.push({
        label: `${movie.label}・評価数`,
        metricKey: "rateCount",
        data: movie.rateCount,
        type: "line",
        borderColor: color,
        borderWidth: 2,
        backgroundColor: "transparent",
        fill: false,
        pointRadius: 2,
        pointStyle: "circle",
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointHoverRadius: 6,
        tension: 0.35,
        spanGaps: true,
        yAxisID: "y2",
        order: 1,
      });
    }
    if (movie.hasRating) {
      datasets.push({
        label: `${movie.label}・評価スコア`,
        metricKey: "rating",
        data: movie.rating,
        type: "line",
        borderColor: scoreColor,
        borderWidth: 2,
        borderDash: [6, 4],
        backgroundColor: "transparent",
        fill: false,
        pointRadius: 3,
        pointStyle: "circle",
        pointBackgroundColor: scoreColor,
        pointBorderColor: scoreColor,
        pointHoverRadius: 6,
        tension: 0.35,
        spanGaps: true,
        yAxisID: "y1",
        order: 0,
      });
    }
    return datasets;
  });

  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  engagementChartInstance.value = new Chart(engagementCtx, {
    type: "bar",
    plugins: [reviewComparisonCompleteTooltipPlugin],
    data: {
      labels: dates,
      datasets: engagementDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...RD_CHART_ANIMATION },
      events: [...RD_CHART_EVENTS],
      transitions: { ...RD_CHART_TRANSITIONS },
      interaction: { ...RD_CHART_INTERACTION },
      elements: { ...RD_CHART_ELEMENTS },
      plugins: {
        legend: reviewComparisonLegend(chartLegend),
        tooltip: reviewComparisonTooltip,
      },
      scales: {
        y: {
          type: "linear",
          display: true,
          position: "left",
          beginAtZero: true,
          grid: { color: chartGrid },
          border: { color: chartTick },
          ticks: {
            color: chartTick,
            font: { weight: "bold" },
            callback: (value) => Number(value).toLocaleString(),
          },
          title: { display: true, text: "観たい", color: chartTick, font: { size: 11, weight: "bold" } },
        },
        y3: {
          type: "linear",
          display: true,
          position: "right",
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          border: { color: chartTick },
          ticks: {
            color: chartTick,
            font: { weight: "bold" },
            callback: (value) => Number(value).toLocaleString(),
          },
          title: { display: true, text: "観た", color: chartTick, font: { size: 11, weight: "bold" } },
        },
        x: {
          grid: { color: chartGrid },
          ticks: { maxTicksLimit: 20, maxRotation: 45, color: chartTick },
        },
      },
    },
  });

  if (seq !== rdChartInitSeq || !rdChartsMounted.value) return;
  ratingChartInstance.value = new Chart(ratingCtx, {
    type: "bar",
    plugins: [reviewComparisonCompleteTooltipPlugin],
    data: {
      labels: dates,
      datasets: ratingDatasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { ...RD_CHART_ANIMATION },
      events: [...RD_CHART_EVENTS],
      transitions: { ...RD_CHART_TRANSITIONS },
      interaction: { ...RD_CHART_INTERACTION },
      elements: { ...RD_CHART_ELEMENTS },
      plugins: {
        legend: reviewComparisonLegend(chartLegend),
        tooltip: reviewComparisonTooltip,
      },
      scales: {
        y2: {
          type: "linear",
          display: true,
          position: "left",
          min: 0,
          beginAtZero: true,
          grid: { color: chartGrid },
          border: { color: chartTick },
          ticks: {
            color: chartTick,
            font: { weight: "bold" },
            precision: 0,
            callback: (value) => (Number.isInteger(value) ? value : ""),
          },
          title: { display: true, text: "評価数", color: chartTick, font: { size: 11, weight: "bold" } },
        },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          min: 0,
          max: 5,
          grid: { drawOnChartArea: false },
          border: { color: chartTick },
          ticks: {
            stepSize: 1,
            color: chartTick,
            font: { weight: "bold" },
            callback: (value) => `${value}★`,
          },
          title: { display: true, text: "評価スコア", color: chartTick, font: { size: 11, weight: "bold" } },
        },
        x: {
          grid: { color: chartGrid },
          ticks: { maxTicksLimit: 20, maxRotation: 45, color: chartTick },
        },
      },
    },
  });
  })();
};

/** loading 解除後に canvas が DOM に載るまで待ってから Chart を生成 */
const mountChartAfterLoading = (mountFn, hasCanvas = () => true, onSettled) => {
  const settle = () => {
    if (onSettled) onSettled();
  };
  const tryMount = () => {
    if (!rdChartsMounted.value || !hasCanvas()) return false;
    mountFn();
    return true;
  };
  nextTick(() => {
    if (tryMount()) {
      settle();
      return;
    }
    // 再等一帧让 canvas 挂载；无论成功与否都结束遮罩，避免卡在 loading
    nextTick(() => {
      tryMount();
      settle();
    });
  });
};

const fetchReviewDaily = async () => {
  loading.value = true;
  chartRendering.value = true;
  reviewChartPack.value = null;
  let rows = [];
  let chartPack = null;
  try {
    const params = {
      movieId: movieId.value,
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
    };
    const selectedRes = await getMovieReviewDailyService(params);
    const selectedList = pickReviewDailyList(selectedRes);
    rows = fillReviewDailyRange(selectedList, dateFrom.value, dateTo.value);
    chartPack = buildReviewChartPack(rows);
    reviewChartPack.value = chartPack;
    dailyData.value = rows;
  } catch {
    reviewChartPack.value = null;
    dailyData.value = [];
  } finally {
    loading.value = false;
  }
  if (rows.length > 0 && chartPack) {
    mountChartAfterLoading(
      () => initCharts(chartPack),
      () => !!(engagementChartRef.value && ratingChartRef.value),
      () => { chartRendering.value = false; },
    );
  } else {
    chartRendering.value = false;
  }
};

const fetchSocialDaily = async () => {
  loading.value = true;
  chartRendering.value = true;
  reviewChartPack.value = null;
  let rows = [];
  let chartPack = null;
  try {
    const selectedRes = await getSocialStatsService({
      movieId: movieId.value,
      startTime: dateFrom.value,
      endTime: dateTo.value,
    });
    const payload = pickSocialStatsPayload(selectedRes);
    const rawRows = payload ? pickSocialDailyList(payload, detailSource.value) : [];
    // 所選作品の X 軸は /api/socialStats/summary が返した日付だけを使用する。
    rows = rawRows;
    chartPack = buildSocialChartPack(rows, detailSource.value);
    dailyData.value = chartPack?.dates?.map((date) => ({ date })) || rows;
  } catch {
    dailyData.value = [];
  } finally {
    loading.value = false;
  }
  if (chartPack) {
    mountChartAfterLoading(
      () => initSocialChart(chartPack, detailSource.value),
      () => !!socialChartRef.value,
      () => { chartRendering.value = false; },
    );
  } else {
    chartRendering.value = false;
  }
};

onMounted(() => {
  resetRdChartDisposeState();
  rdChartInitSeq = 0;
  rdChartsMounted.value = true;
  updateRotateHint();
  window.addEventListener("resize", updateRotateHint);
  window.addEventListener("orientationchange", updateRotateHint);
  if (!movieId.value) return;
  if (isSocialDetail.value) {
    fetchSocialDaily();
  } else {
    fetchReviewDaily();
  }
});

watch(
  () => [
    movieId.value,
    dateFrom.value,
    dateTo.value,
    detailSource.value,
  ].join("|"),
  (cur, prev) => {
    if (!movieId.value || prev == null) return;
    if (cur === prev) return;
    if (isSocialDetail.value) fetchSocialDaily();
    else fetchReviewDaily();
  },
);

const disposeChart = () => {
  if (rdChartDisposePromise) return rdChartDisposePromise;
  rdChartInitSeq += 1;
  rdChartsMounted.value = false;
  const work = (async () => {
    await nextTick();
    await destroyRdChartInstance(engagementChartInstance, engagementChartRef.value);
    await destroyRdChartInstance(ratingChartInstance, ratingChartRef.value);
    await destroyRdChartInstance(socialChartInstance, socialChartRef.value);
    await destroyAllChartsUnderSelector('.review-detail-page');
    await waitRdAnimationFrames(3);
  })();
  rdChartDisposePromise = work;
  markClientV2DetailChartsDisposing(work);
  return work;
};

// 路由离开前销毁 chart，确保 index onActivated 时无残留 Animator
onBeforeRouteLeave(async () => {
  closeRdFullscreenImmediate();
  await disposeChart();
});

const goBack = async () => {
  closeRdFullscreenImmediate();
  await disposeChart();
  await waitRdAnimationFrames(2);
  if (window.history.length > 1) {
    router.back();
    return;
  }
  router.push("/client/client-v2");
};

onBeforeUnmount(() => {
  closeRdFullscreenImmediate();
  void disposeChart();
});

onUnmounted(() => {
  window.removeEventListener("resize", updateRotateHint);
  window.removeEventListener("orientationchange", updateRotateHint);
});

const formatNum = (n) =>
  n != null ? Number(n).toLocaleString() : "—";
</script>

<template>
  <div class="review-detail-page">
    <div v-if="showRotateHint" class="rd-rotate-top-hint" aria-live="polite">
      <i class="fas fa-mobile-alt hint-phone"></i>
      <span>横向きで見ると見やすくなります</span>
    </div>
    <!-- Page Header -->
    <div class="rd-header">
      <button class="rd-back-btn" @click="goBack">
        <i class="fas fa-arrow-left"></i>
        <span>戻る</span>
      </button>
      <div class="rd-header-center">
        <h1 class="rd-title">
          <XPlatformIcon
            v-if="detailSource === 'twitter'"
            class="rd-title-icon rd-title-icon--twitter"
            size="1.2rem"
          />
          <i
            v-else
            :class="[pageTitleIcon, 'rd-title-icon', `rd-title-icon--${detailSource}`]"
          ></i>
          {{ pageTitle }}
        </h1>
        <div class="rd-subtitle" v-if="movieName || dateLabel || instagramHashtag || twitterHashtag">
          <span v-if="movieName" class="rd-movie-name">{{ movieName }}</span>
          <span v-if="detailSource === 'instagram' && instagramHashtag" class="rd-hashtag-label">
            <i class="fas fa-hashtag"></i>
            {{ instagramHashtag }}
          </span>
          <span v-if="detailSource === 'twitter' && twitterHashtag" class="rd-hashtag-label rd-hashtag-label--twitter">
            <i class="fas fa-hashtag"></i>
            {{ twitterHashtag }}
          </span>
          <span v-if="dateLabel" class="rd-date-label">
            <i class="fas fa-calendar-alt"></i>
            {{ dateLabel }}
          </span>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="rd-stats-grid" style="display:none;">
      <div class="rd-stat-card rd-stat--blue">
        <div class="rd-stat-icon"><i class="fas fa-star-half-alt"></i></div>
        <div class="rd-stat-body">
          <div class="rd-stat-label">期間内 評価数</div>
          <div class="rd-stat-value">{{ formatNum(totalRateCount) }}</div>
        </div>
      </div>
      <div class="rd-stat-card rd-stat--red">
        <div class="rd-stat-icon"><i class="fas fa-star"></i></div>
        <div class="rd-stat-body">
          <div class="rd-stat-label">加重平均スコア</div>
          <div class="rd-stat-value">{{ avgRating ? `${avgRating} ★` : "—" }}</div>
        </div>
      </div>
      <div class="rd-stat-card rd-stat--green">
        <div class="rd-stat-icon"><i class="fas fa-heart"></i></div>
        <div class="rd-stat-body">
          <div class="rd-stat-label">みたい（最新）</div>
          <div class="rd-stat-value">{{ formatNum(latestWantToSee) }}</div>
        </div>
      </div>
      <div class="rd-stat-card rd-stat--purple">
        <div class="rd-stat-icon"><i class="fas fa-check-circle"></i></div>
        <div class="rd-stat-body">
          <div class="rd-stat-label">みた（最新）</div>
          <div class="rd-stat-value">{{ formatNum(latestHaveSeen) }}</div>
        </div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="rd-card rd-chart-card">
      <div class="rd-card-header">
        <span class="card-header-title-with-help" @click.stop>
          <XPlatformIcon
            v-if="detailSource === 'twitter'"
            class="rd-title-icon rd-title-icon--twitter"
            size="0.92rem"
          />
          <i
            v-else
            :class="isSocialDetail
              ? [pageTitleIcon, 'rd-title-icon', `rd-title-icon--${detailSource}`]
              : 'fas fa-chart-bar'"
          ></i>
          <el-tooltip
            :content="chartSectionTitle"
            placement="top"
            trigger="hover"
            :show-after="200"
          >
            <span class="card-header-title-text">{{ chartSectionTitle }}</span>
          </el-tooltip>
          <el-popover
            v-model:visible="chartDataTipVisible"
            placement="top"
            width="auto"
            trigger="click"
            popper-class="social-data-source-popover"
          >
            <template #reference>
              <button
                type="button"
                class="rating-help-btn"
                title="データ出所"
                @click.stop
              >
                ?
              </button>
            </template>
            <template #default>
              <p class="social-data-source-tip">{{ SOCIAL_DATA_SOURCE_TIP }}</p>
            </template>
          </el-popover>
        </span>
      </div>
      <div class="rd-chart-card-body">
      <div v-if="loading || chartRendering" class="rd-chart-loading rd-chart-loading--overlay">
        <i class="fas fa-spinner fa-spin"></i>
        <span>データを読み込み中...</span>
      </div>
      <div v-if="!loading && !chartRendering && dailyData.length === 0" class="rd-chart-empty">
        <i class="fas fa-chart-area"></i>
        <span>データがありません</span>
      </div>
      <div v-else-if="isSocialDetail" class="rd-chart-container rd-chart-container--social">
        <div
          class="rd-chart-block rd-chart-block--social"
          data-rd-chart-key="social"
          :class="{
            'rd-chart-block--fullscreen': rdFullscreenChartKey === 'social',
            'rd-chart-block--fullscreen-closing':
              rdFullscreenClosing && rdFullscreenChartKey === 'social',
          }"
        >
          <div class="rd-chart-block__head">
            <span class="rd-chart-title">{{ chartSectionTitle }}</span>
            <div class="rd-chart-block__actions">
              <button
                v-if="device === 'mobile' && (!rdFullscreenChartKey || rdFullscreenChartKey === 'social')"
                type="button"
                class="rd-chart-fs-btn"
                :aria-label="rdFullscreenChartKey === 'social' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  rdFullscreenChartKey === 'social' ? closeRdFullscreen() : openRdFullscreen('social')
                "
              >
                <i :class="rdFullscreenChartKey === 'social' ? 'fas fa-compress' : 'fas fa-expand'"></i>
              </button>
            </div>
          </div>
          <div class="rd-chart-canvas-wrap">
            <canvas v-if="rdChartsMounted" ref="socialChartRef"></canvas>
          </div>
        </div>
      </div>
      <div v-else class="rd-chart-container">
        <div
          class="rd-chart-block"
          data-rd-chart-key="engagement"
          :class="{
            'rd-chart-block--fullscreen': rdFullscreenChartKey === 'engagement',
            'rd-chart-block--fullscreen-closing':
              rdFullscreenClosing && rdFullscreenChartKey === 'engagement',
          }"
        >
          <div class="rd-chart-block__head">
            <span class="rd-chart-title">観たい / 観た</span>
            <div class="rd-chart-block__actions">
              <button
                v-if="
                  device === 'mobile' &&
                  (!rdFullscreenChartKey || rdFullscreenChartKey === 'engagement')
                "
                type="button"
                class="rd-chart-fs-btn"
                :aria-label="rdFullscreenChartKey === 'engagement' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  rdFullscreenChartKey === 'engagement'
                    ? closeRdFullscreen()
                    : openRdFullscreen('engagement')
                "
              >
                <i
                  :class="rdFullscreenChartKey === 'engagement' ? 'fas fa-compress' : 'fas fa-expand'"
                ></i>
              </button>
            </div>
          </div>
          <div class="rd-chart-canvas-wrap">
            <canvas v-if="rdChartsMounted" ref="engagementChartRef"></canvas>
          </div>
        </div>
        <div
          class="rd-chart-block"
          data-rd-chart-key="rating"
          :class="{
            'rd-chart-block--fullscreen': rdFullscreenChartKey === 'rating',
            'rd-chart-block--fullscreen-closing':
              rdFullscreenClosing && rdFullscreenChartKey === 'rating',
          }"
        >
          <div class="rd-chart-block__head">
            <span class="rd-chart-title">評価数 / スコア</span>
            <div class="rd-chart-block__actions">
              <button
                v-if="device === 'mobile' && (!rdFullscreenChartKey || rdFullscreenChartKey === 'rating')"
                type="button"
                class="rd-chart-fs-btn"
                :aria-label="rdFullscreenChartKey === 'rating' ? '全画面を終了' : '全画面表示'"
                @click.stop="
                  rdFullscreenChartKey === 'rating' ? closeRdFullscreen() : openRdFullscreen('rating')
                "
              >
                <i
                  :class="rdFullscreenChartKey === 'rating' ? 'fas fa-compress' : 'fas fa-expand'"
                ></i>
              </button>
            </div>
          </div>
          <div class="rd-chart-canvas-wrap">
            <canvas v-if="rdChartsMounted" ref="ratingChartRef"></canvas>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Daily Data Table -->
    <div class="rd-card rd-table-card" style="display:none;">
      <div class="rd-card-header">
        <span><i class="fas fa-table"></i> 日別データ一覧</span>
        <span class="rd-table-count">{{ dailyData.length }} 件</span>
      </div>
      <div class="rd-table-wrap">
        <table class="rd-table">
          <thead>
            <tr>
              <th>日付</th>
              <th>評価スコア</th>
              <th>評価数</th>
              <th>観たい</th>
              <th>観た</th>
              <th>★5</th>
              <th>★4</th>
              <th>★3</th>
              <th>★2</th>
              <th>★1</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in dailyData" :key="row.date">
              <td class="rd-td-date">{{ row.date }}</td>
              <td class="rd-td-score">
                <span v-if="row.rating != null" class="rd-score-badge">{{ Number(row.rating).toFixed(1) }}</span>
                <span v-else class="rd-td-empty">—</span>
              </td>
              <td class="rd-td-blue">{{ formatNum(row.rateCount) }}</td>
              <td class="rd-td-green">{{ formatNum(row.wantToSee) }}</td>
              <td class="rd-td-purple">{{ formatNum(row.haveSeen) }}</td>
              <td>{{ formatNum(row.rate5Count) }}</td>
              <td>{{ formatNum(row.rate4Count) }}</td>
              <td>{{ formatNum(row.rate3Count) }}</td>
              <td>{{ formatNum(row.rate2Count) }}</td>
              <td>{{ formatNum(row.rate1Count) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="device === 'mobile' && rdFullscreenChartKey"
      class="rd-chart-fs-backdrop"
      :class="{ 'rd-chart-fs-backdrop--closing': rdFullscreenClosing }"
      aria-hidden="true"
      @click="closeRdFullscreen"
    />
  </div>
</template>

<style lang="less" scoped>
.review-detail-page {
  min-height: 100vh;
  background: var(--bg-body, #f5f6fa);
  padding: 28px 32px 48px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 16px 40px;
  }

  .rd-chart-fs-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: linear-gradient(
      165deg,
      rgba(8, 10, 28, 0.62) 0%,
      rgba(18, 14, 40, 0.55) 45%,
      rgba(12, 12, 22, 0.5) 100%
    );
    backdrop-filter: blur(10px) saturate(1.15);
    -webkit-backdrop-filter: blur(10px) saturate(1.15);
    animation: rdChartFsBackdropIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    transform: translateZ(0);

    &--closing {
      animation: rdChartFsBackdropOut 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      pointer-events: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .rd-chart-fs-backdrop {
      animation: none;
      opacity: 1;
    }

    .rd-chart-fs-backdrop--closing {
      opacity: 0;
    }
  }
}

// ─── Header ───────────────────────────────────────────────
.rd-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.rd-rotate-hint {
  display: none;
}

.rd-rotate-top-hint {
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    position: sticky;
    top: 8px;
    z-index: 20;
    margin: -6px 0 10px;
    padding: 7px 10px;
    border-radius: 999px;
    border: 1px solid color-mix(in srgb, var(--brand-color) 72%, transparent);
    background: linear-gradient(90deg, color-mix(in srgb, var(--brand-color) 20%, transparent), rgba(255, 120, 30, 0.14));
    color: #5e4000;
    font-size: 0.74rem;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.07);
    animation: hintEnter 0.45s ease-out, hintPulseStrong 1.1s ease-in-out infinite 0.45s;

    .hint-phone {
      color: #b28700;
      font-size: 0.76rem;
      animation: phoneFloatStrong 0.9s ease-in-out infinite;
    }
  }
}

/* dark：回転ヒントを主题のテキスト色へ追従（暗背景に対してコントラスト確保） */
html.dark .rd-rotate-top-hint {
  color: var(--text-primary, #f0f0f0);

  .hint-phone {
    color: var(--text-secondary, #a8adb8);
  }
}

@keyframes hintEnter {
  from {
    opacity: 0;
    transform: translateY(-14px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes hintPulseStrong {
  0%, 100% {
    transform: translateY(0);
    box-shadow: 0 4px 10px rgba(255, 145, 0, 0.1);
  }
  50% {
    transform: translateY(-3px);
    box-shadow: 0 8px 14px rgba(255, 145, 0, 0.16);
  }
}

@keyframes phoneFloatStrong {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-2px) rotate(-6deg); }
  75% { transform: translateY(-2px) rotate(6deg); }
}

.rd-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border: 1.5px solid var(--border-color, #e0e0e0);
  border-radius: 50px;
  background: var(--bg-panel, #fff);
  color: var(--text-secondary, #666);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: var(--brand-color, #f9da49);
    color: var(--text-primary, #181818);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  }
}

.rd-header-center {
  flex: 1;
  min-width: 0;
}

.rd-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-primary, #181818);
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
}

.rd-title-icon {
  color: var(--brand-color, #f9da49);
  font-size: 1.2rem;

  &--youtube {
    color: #ff0000;
  }

  &--instagram {
    color: #e4405f;
  }

  &--twitter {
    color: #0f1419;
  }
}

.rd-hashtag-label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #e4405f;

  &--twitter {
    color: #0f1419;
  }
}

html.dark .review-detail-page {
  .rd-title-icon--twitter,
  .rd-hashtag-label--twitter {
    color: #e7e9ea;
  }

  .rd-card-header .rd-title-icon--twitter {
    color: #e7e9ea;
  }
}

.rd-subtitle {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.rd-movie-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary, #181818);
  background: linear-gradient(90deg, var(--brand-color, #f9da49), var(--accent-orange, #ff9500));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.rd-date-label {
  font-size: 0.85rem;
  color: var(--text-secondary, #888);
  display: flex;
  align-items: center;
  gap: 5px;
}

// ─── Stats Grid ───────────────────────────────────────────
.rd-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

.rd-stat-card {
  background: var(--bg-panel, #fff);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    border-radius: 14px 14px 0 0;
  }

  &.rd-stat--blue::before { background: #0066FF; }
  &.rd-stat--red::before { background: #EF4444; }
  &.rd-stat--green::before { background: #10B981; }
  &.rd-stat--purple::before { background: #8B5CF6; }
}

.rd-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;

  .rd-stat--blue & { background: rgba(0, 102, 255, 0.1); color: #0066FF; }
  .rd-stat--red & { background: rgba(239, 68, 68, 0.1); color: #EF4444; }
  .rd-stat--green & { background: rgba(16, 185, 129, 0.1); color: #10B981; }
  .rd-stat--purple & { background: rgba(139, 92, 246, 0.1); color: #8B5CF6; }
}

.rd-stat-label {
  font-size: 0.78rem;
  color: var(--text-secondary, #888);
  margin-bottom: 4px;
  font-weight: 500;
}

.rd-stat-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text-primary, #181818);
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
}

// ─── Cards ────────────────────────────────────────────────
.rd-card {
  background: var(--bg-panel, #fff);
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.rd-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text-primary, #181818);
  background: var(--bg-panel, #fff);

  i {
    color: var(--brand-color, #f9da49);
    margin-right: 8px;
  }

  .rd-title-icon--youtube {
    color: #ff0000;
  }

  .rd-title-icon--instagram {
    color: #e4405f;
  }

  .rd-title-icon--twitter {
    color: #0f1419;
  }
}

// ─── Header title + help（index.vue の card-header-title-with-help と同一様式）──
.card-header-title-with-help {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  position: relative;
  line-height: 1.45;

  .card-header-title-text {
    display: inline;
    cursor: default;
    transition: color 0.28s ease;
  }

  .rating-help-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-left: 6px;
    border-radius: 50%;
    border: 1.5px solid var(--brand-color, #f9b749);
    background: transparent;
    color: var(--brand-color, #f9b749);
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: var(--brand-color, #f9b749);
      color: #181818;
    }
  }

  :deep(.el-tooltip__trigger) {
    display: inline;
    vertical-align: middle;
  }
}

.rd-table-count {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary, #888);
  background: var(--bg-body, #f5f6fa);
  padding: 3px 10px;
  border-radius: 20px;
}

// ─── Chart ────────────────────────────────────────────────
@keyframes rdChartFsIn {
  0% {
    opacity: 0;
    transform: translate3d(0, 18px, 0) scale(0.92);
    filter: brightness(0.88) saturate(0.95);
  }

  55% {
    opacity: 1;
    transform: translate3d(0, -4px, 0) scale(1.012);
    filter: brightness(1.02) saturate(1);
  }

  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    filter: brightness(1) saturate(1);
  }
}

@keyframes rdChartFsOut {
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
    filter: brightness(1);
  }

  to {
    opacity: 0;
    transform: translate3d(0, 14px, 0) scale(0.94);
    filter: brightness(0.92);
  }
}

@keyframes rdChartFsBackdropIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes rdChartFsBackdropOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes rdChartFsHeaderIn {
  from {
    opacity: 0;
    transform: translate3d(0, -10px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.rd-chart-fs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.06);
  color: var(--text-secondary, #555);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:active {
    transform: scale(0.94);
  }

  .rd-chart-block--fullscreen & {
    background: rgba(0, 0, 0, 0.08);
  }
}

html.dark .review-detail-page .rd-chart-fs-btn {
  background: rgba(255, 255, 255, 0.1);

  .rd-chart-block--fullscreen & {
    background: rgba(255, 255, 255, 0.14);
  }
}

.rd-chart-card {
  .rd-chart-container {
    padding: 20px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;

    @media (max-width: 768px) {
      padding: 16px;
    }

    &--social .rd-chart-block--social {
      height: 380px;

      @media (max-width: 768px) {
        height: 300px;
      }
    }

    .rd-chart-block {
      display: flex;
      flex-direction: column;
      height: 320px;
      box-sizing: border-box;
      border-radius: 12px;
      border: 1px solid var(--border-color, #e8e8e8);
      padding: 12px 14px;
      background: var(--bg-panel, #fff);
    }

    .rd-chart-block__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-shrink: 0;
      margin-bottom: 8px;
    }

    .rd-chart-title {
      font-size: 0.86rem;
      font-weight: 700;
      color: var(--text-secondary, #666);
      margin: 0;
      flex: 1 1 auto;
      min-width: 0;
      word-break: break-word;
    }

    // 全画面ボタン領域は縮まない（長いタイトルに押されてボタンが欠けるのを防ぐ。
    // index.vue の .card-header-chart-actions と同じ対応）
    .rd-chart-block__actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .rd-chart-canvas-wrap {
      flex: 1;
      min-height: 0;
      position: relative;
      overflow: hidden;
    }

    canvas {
      width: 100% !important;
      height: 100% !important;
    }

    .rd-chart-block--fullscreen {
      position: fixed;
      z-index: 10001;
      inset: 0;
      width: 100vw;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      max-width: 100vw;
      max-height: 100vh;
      margin: 0;
      border-radius: 0;
      height: auto !important;
      min-height: 0 !important;
      padding:
        max(12px, env(safe-area-inset-top, 0px))
        max(12px, env(safe-area-inset-right, 0px))
        max(12px, env(safe-area-inset-bottom, 0px))
        max(12px, env(safe-area-inset-left, 0px));
      transform-origin: 50% 45%;
      animation: rdChartFsIn 0.72s cubic-bezier(0.22, 1, 0.36, 1) forwards;

      .rd-chart-block__head {
        animation: rdChartFsHeaderIn 0.62s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
      }

      .rd-chart-canvas-wrap {
        flex: 1;
        min-height: 0;
      }
    }

    .rd-chart-block--fullscreen-closing {
      animation: rdChartFsOut 0.62s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      .rd-chart-block--fullscreen {
        animation: none;
        opacity: 1;
        filter: none;

        .rd-chart-block__head {
          animation: none;
        }
      }

      .rd-chart-block--fullscreen-closing {
        animation: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
    }
  }
}

.rd-chart-loading,
.rd-chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 200px;
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;

  i {
    font-size: 2rem;
    color: var(--border-color, #ddd);
  }
}

/* 图表主体：作为 loading 遮罩的定位容器 */
.rd-chart-card-body {
  position: relative;
}

/* 数据加载 + chart 渲染完成前的遮罩，覆盖在 canvas 之上 */
.rd-chart-loading--overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  height: auto;
  min-height: 200px;
  background: var(--bg-panel, #fff);
}

// ─── Table ────────────────────────────────────────────────
.rd-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.rd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  white-space: nowrap;

  th {
    padding: 11px 14px;
    text-align: center;
    font-weight: 700;
    font-size: 0.8rem;
    color: var(--text-secondary, #888);
    background: var(--bg-body, #f5f6fa);
    border-bottom: 1px solid var(--border-color, #e0e0e0);
    position: sticky;
    top: 0;
  }

  td {
    padding: 10px 14px;
    text-align: center;
    border-bottom: 1px solid var(--border-color, #f0f0f0);
    color: var(--text-primary, #333);
  }

  tbody tr:hover {
    background: color-mix(in srgb, var(--text-primary) 4%, transparent);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.rd-td-date {
  font-weight: 600;
  color: var(--text-primary, #181818) !important;
  text-align: left !important;
  padding-left: 20px !important;
  font-size: 0.88rem;
}

.rd-score-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
  font-weight: 700;
  font-size: 0.88rem;
}

.rd-td-empty {
  color: var(--text-secondary, #bbb);
}

.rd-td-blue { color: #0066FF; font-weight: 600; }
.rd-td-green { color: #10B981; font-weight: 600; }
.rd-td-purple { color: #8B5CF6; font-weight: 600; }
</style>

<!-- el-popover は body へ teleport されるため、非 scoped で指定する -->
<style lang="less">
.social-data-source-popover {
  padding: 10px 12px !important;
  max-width: 280px;

  .social-data-source-tip {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: var(--text-primary, #333);
  }
}

html.dark .social-data-source-popover .social-data-source-tip {
  color: var(--text-primary, #f0f0f0);
}
</style>
